import queryString from 'query-string';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { VERSION_QUERY_PARAM } from 'common/constants/pageFilters';
import { TAB_QUERY_PARAM } from 'pages/ValuationsAllocation/common/constants/valuations';
import { removeWaterfallCalculationQueryParams } from 'pages/Waterfall/utilities/waterfallSearchParams';
import type { BuildCompanyNavigationUrlParams } from './companyNavigation.types';

const omitQueryParams = (queryParams: Record<string, unknown>, paramsToRemove: string[]) =>
  Object.fromEntries(Object.entries(queryParams).filter(([param]) => !paramsToRemove.includes(param)));

export const getCompanyNavigationQueryParams = ({
  currentTableSlug,
  queryParams,
  targetTableSlug,
}: Pick<BuildCompanyNavigationUrlParams, 'currentTableSlug' | 'queryParams' | 'targetTableSlug'>) => {
  let nextQuery = { ...queryParams };

  // A carried date query represents the selected measurement date; version/tab params
  // belong to the previous page state and should not leak into the next company tab.
  if (nextQuery.date) {
    nextQuery = omitQueryParams(nextQuery, [VERSION_QUERY_PARAM, TAB_QUERY_PARAM]);
  }

  // Waterfall calculation params are only valid inside Waterfall. Leaving that tab
  // should keep shared page params but clear calculation-specific inputs.
  if (currentTableSlug === WATERFALL_SLUG && targetTableSlug !== WATERFALL_SLUG) {
    nextQuery = removeWaterfallCalculationQueryParams(nextQuery);
  }

  return nextQuery;
};

export const buildCompanyNavigationUrl = ({
  companySlug,
  currentTableSlug,
  firmSlug,
  getUrl,
  queryParams,
  targetTableSlug,
}: BuildCompanyNavigationUrlParams) => {
  // Company tabs keep their legacy company route until SCAL-8587 moves selection into the Prism page/tab model.
  const pathname = getUrl(firmSlug, companySlug);
  // Keep query retention centralized so sidebar, secondary nav, and future menu entries
  // do not drift on which tab-specific params are preserved.
  const nextQuery = getCompanyNavigationQueryParams({
    currentTableSlug,
    queryParams,
    targetTableSlug,
  });
  const search = queryString.stringify(nextQuery);

  return search ? `${pathname}?${search}` : pathname;
};
